// TOUR-2 — highlight a DOM target with Teaching/Guidance intent (no mutations).

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { AuthContextValue } from "@/contexts/AuthContext";
import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { useAuthOptional } from "@/contexts/AuthContext";
import type { FireflyIntent } from "./fireflyIntents";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";
import { FireflyTargetHighlightOverlay } from "./FireflyTargetHighlightOverlay";
import {
  canHighlightFireflyTarget,
  type CanAccessPrimaryModeFn,
} from "@/lib/firefly/canHighlightFireflyTarget";
import {
  queryFireflyTargetById,
  readFireflyTargetRect,
  type FireflyTargetRect,
} from "@/lib/firefly/fireflyTargetConvention";

export const FIREFLY_TARGET_HIGHLIGHT_DEFAULT_MS = 8_000;

export type UseFireflyTargetHighlightOptions = {
  /** `data-firefly-target` id — preferred convention. */
  targetId?: string | null;
  /** Direct element ref when the consumer owns the node. */
  elementRef?: RefObject<HTMLElement | null>;
  intent?: Extract<FireflyIntent, "teaching" | "guidance" | "discovery">;
  /** When false, clears highlight state. */
  active: boolean;
  auth?: AuthContextValue;
  canAccessPrimaryMode?: CanAccessPrimaryModeFn;
  autoDismissMs?: number;
  callout?: { title: string; body: string };
};

export type UseFireflyTargetHighlightResult = {
  isVisible: boolean;
  denied: boolean;
  missing: boolean;
  rect: FireflyTargetRect | null;
  dismiss: () => void;
  overlay: React.ReactNode;
};

function resolveHighlightElement(
  targetId: string | null | undefined,
  elementRef: RefObject<HTMLElement | null> | undefined,
): HTMLElement | null {
  if (elementRef?.current) return elementRef.current;
  if (targetId) return queryFireflyTargetById(targetId);
  return null;
}

export function useFireflyTargetHighlight(
  options: UseFireflyTargetHighlightOptions,
): UseFireflyTargetHighlightResult {
  const {
    targetId = null,
    elementRef,
    intent = "teaching",
    active,
    auth: authProp,
    canAccessPrimaryMode: canAccessFn = canAccessPrimaryMode,
    autoDismissMs = FIREFLY_TARGET_HIGHLIGHT_DEFAULT_MS,
    callout,
  } = options;

  const authCtx = useAuthOptional();
  const auth = authProp ?? authCtx;
  const reducedMotion = usePrefersReducedMotion();
  const [generation, setGeneration] = useState(0);
  const [rect, setRect] = useState<FireflyTargetRect | null>(null);
  const [denied, setDenied] = useState(false);
  const [missing, setMissing] = useState(false);
  const [visible, setVisible] = useState(false);
  const dismissTimerRef = useRef<number | null>(null);

  const dismiss = useCallback(() => {
    if (dismissTimerRef.current != null) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    setVisible(false);
    setRect(null);
    setDenied(false);
    setMissing(false);
  }, []);

  const measure = useCallback(() => {
    const el = resolveHighlightElement(targetId, elementRef);
    if (!el) {
      setMissing(true);
      setVisible(false);
      setRect(null);
      return;
    }
    setRect(readFireflyTargetRect(el));
  }, [elementRef, targetId]);

  useEffect(() => {
    if (!active) {
      dismiss();
      return;
    }
    if (!targetId && !elementRef) {
      dismiss();
      return;
    }
    if (auth?.status !== "ready") {
      dismiss();
      return;
    }

    if (targetId) {
      const gate = canHighlightFireflyTarget({
        targetId,
        auth,
        canAccessPrimaryMode: canAccessFn,
      });
      if (!gate.allowed) {
        setDenied(true);
        setMissing(false);
        setVisible(false);
        setRect(null);
        return;
      }
    }

    const el = resolveHighlightElement(targetId, elementRef);
    if (!el) {
      setMissing(true);
      setDenied(false);
      setVisible(false);
      setRect(null);
      return;
    }

    setDenied(false);
    setMissing(false);
    setVisible(true);
    setGeneration((g) => g + 1);
    measure();

    dismissTimerRef.current = window.setTimeout(dismiss, autoDismissMs);

    const onLayout = () => measure();
    window.addEventListener("scroll", onLayout, true);
    window.addEventListener("resize", onLayout);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onLayout) : null;
    ro?.observe(el);

    return () => {
      window.removeEventListener("scroll", onLayout, true);
      window.removeEventListener("resize", onLayout);
      ro?.disconnect();
      if (dismissTimerRef.current != null) {
        window.clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
    };
  }, [
    active,
    auth,
    autoDismissMs,
    canAccessFn,
    dismiss,
    elementRef,
    measure,
    targetId,
  ]);

  const overlay = useMemo(() => {
    if (!visible || !rect || denied || missing) return null;
    return (
      <FireflyTargetHighlightOverlay
        rect={rect}
        intent={intent}
        reducedMotion={reducedMotion}
        highlightGeneration={generation}
        callout={callout}
      />
    );
  }, [callout, denied, generation, intent, missing, rect, reducedMotion, visible]);

  return {
    isVisible: visible && !denied && !missing,
    denied,
    missing,
    rect,
    dismiss,
    overlay,
  };
}
